import {useCallback, useMemo, type SyntheticEvent} from 'react'
import {Box, Typography, Checkbox, FormControlLabel} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../common/store'
import {updateCustomDecal, updateCustomDecalMaterial} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {CustomDecal} from '../../../common/types/customDecalTypes'
import {customDecalPlaneSize, maxCustomDecalSize, MIN_CUSTOM_DECAL_SIZE} from '../../../common/types/customDecalTypes'
import type {Sprite3DMaterial} from '../../../common/types/spriteTypes'
import {
  setPreviewCustomDecal,
  clearPreviewCustomDecal,
  type PreviewCustomDecal,
} from '../../../common/components/deviceMesh/previewCustomDecal'
import {useAuth} from '../../auth/useAuth'
import {uploadAsset} from '../../../common/api/uploadAsset'
import {cleanupReplacedAsset} from '../../../common/api/cleanupReplacedAsset'
import {loadImageAsDataUrl, measureImageAspect} from '../../../common/utils/spriteUpload'
import {DEFAULT_SPRITE_IMAGE_URL} from '../../../common/constants/defaultAssets'
import {MAX_DECAL_IMAGE_SIZE_BYTES} from '../../../common/constants/assetUpload'
import SpriteDropZone from '../rightSidebar/SpriteDropZone'
import OffsetPad from '../sharedWidgets/OffsetPad'
import SliderRow from '../sharedWidgets/SliderRow'

interface CustomDecalItemProps {
  decal: CustomDecal
  unit: string
  deviceWidth: number
  deviceHeight: number
  deviceDepth: number
  deviceEdgeBevel: number
  deviceCornerRadius: number
}

const round1 = (v: number): number => Math.round(v * 10) / 10

const CustomDecalItem = ({
  decal,
  unit,
  deviceWidth,
  deviceHeight,
  deviceDepth,
  deviceEdgeBevel,
  deviceCornerRadius,
}: CustomDecalItemProps) => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const {user} = useAuth()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  // ── Drag → preview store (smooth); release → Redux commit ──────────
  const previewBase = useMemo(
    (): PreviewCustomDecal => ({
      offsetU: decal.offsetU,
      offsetV: decal.offsetV,
      size: decal.size,
      rotation: decal.rotation,
    }),
    [decal.offsetU, decal.offsetV, decal.size, decal.rotation],
  )
  const preview = useCallback(
    (changes: Partial<PreviewCustomDecal>) => setPreviewCustomDecal(decal.id, previewBase, changes),
    [decal.id, previewBase],
  )
  const commit = useCallback(
    (changes: Partial<CustomDecal>) => dispatch(updateCustomDecal({id: decal.id, changes})),
    [dispatch, decal.id],
  )
  const endDrag = useCallback(() => {
    clearPreviewCustomDecal()
    dispatch(sealUndo())
  }, [dispatch])

  const handleMaterialChange = useCallback(
    (changes: Partial<Sprite3DMaterial>) => {
      dispatch(updateCustomDecalMaterial({id: decal.id, changes}))
    },
    [dispatch, decal.id],
  )
  const handleSealUndo = useCallback(() => dispatch(sealUndo()), [dispatch])

  const handleApplyFile = useCallback(
    async (file: File) => {
      let imageKey: string | null = null
      let imageUrl: string
      if (user) {
        const result = await uploadAsset(file, 'decal')
        imageKey = result.key
        imageUrl = result.url
        cleanupReplacedAsset(decal.imageKey)
      } else {
        imageUrl = await loadImageAsDataUrl(file)
      }
      const imageAspect = await measureImageAspect(imageUrl)
      dispatch(updateCustomDecal({id: decal.id, changes: {imageKey, imageUrl, imageAspect}}))
      dispatch(sealUndo())
    },
    [dispatch, decal.id, decal.imageKey, user],
  )

  const handleToggleUnlit = useCallback(
    (_: SyntheticEvent, checked: boolean) => {
      dispatch(updateCustomDecal({id: decal.id, changes: {unlit: checked}}))
      dispatch(sealUndo())
    },
    [dispatch, decal.id],
  )

  // ── Limits — mirror clampCustomDecals in scenesSlice ───────────────
  const isFront = decal.placement === 'front'
  const isHorizontalEdge = decal.placement === 'left' || decal.placement === 'right'
  const clampedBevel = Math.min(deviceEdgeBevel, deviceWidth / 2, deviceHeight / 2, deviceDepth / 2)

  // Surface extents (U, V) for the size cap.
  const {su, sv} = useMemo(() => {
    if (isFront) {
      return {su: deviceWidth - 2 * clampedBevel, sv: deviceHeight - 2 * clampedBevel}
    }
    if (isHorizontalEdge) {
      return {su: deviceDepth, sv: deviceHeight - 2 * deviceCornerRadius}
    }
    return {su: deviceWidth - 2 * deviceCornerRadius, sv: deviceDepth}
  }, [isFront, isHorizontalEdge, deviceWidth, deviceHeight, deviceDepth, clampedBevel, deviceCornerRadius])
  const sizeMax = round1(maxCustomDecalSize(decal.imageAspect, su, sv))

  const [planeW, planeH] = useMemo(() => customDecalPlaneSize(decal), [decal])
  const halfU = planeW / 2
  const halfV = planeH / 2

  const frontLimU = round1(Math.max(0, deviceWidth / 2 - clampedBevel - halfU))
  const frontLimV = round1(Math.max(0, deviceHeight / 2 - clampedBevel - halfV))
  const alongLim = round1(
    isHorizontalEdge
      ? Math.max(0, deviceHeight / 2 - deviceCornerRadius - halfV)
      : Math.max(0, deviceWidth / 2 - deviceCornerRadius - halfU),
  )
  // Side decals stay centered on the face thickness — only the along-edge position is editable.
  const alongValue = isHorizontalEdge ? decal.offsetV : decal.offsetU
  const alongKey: keyof PreviewCustomDecal = isHorizontalEdge ? 'offsetV' : 'offsetU'

  return (
    <Box sx={sx.root}>
      <SpriteDropZone
        imageUrl={decal.imageUrl}
        isDefaultImage={decal.imageUrl === DEFAULT_SPRITE_IMAGE_URL}
        maxSizeBytes={MAX_DECAL_IMAGE_SIZE_BYTES}
        onApplyFile={handleApplyFile}
      />

      <SliderRow
        label={t('device.customDecal.size')}
        value={Math.min(decal.size, sizeMax)}
        min={MIN_CUSTOM_DECAL_SIZE}
        max={sizeMax}
        step={0.5}
        unit={unit}
        onChange={(v: number) => preview({size: v})}
        onChangeCommitted={(v: number) => {
          commit({size: v})
          endDrag()
        }}
      />

      <SliderRow
        label={t('device.customDecal.rotation')}
        value={decal.rotation}
        min={-180}
        max={180}
        step={1}
        unit="°"
        onChange={(v: number) => preview({rotation: v})}
        onChangeCommitted={(v: number) => {
          commit({rotation: v})
          endDrag()
        }}
      />

      <Typography sx={sx.sectionLabel}>{t('device.customDecal.position')}</Typography>
      {isFront ? (
        <OffsetPad
          valueX={decal.offsetU}
          valueY={-decal.offsetV}
          min={-frontLimV}
          max={frontLimV}
          minX={-frontLimU}
          maxX={frontLimU}
          step={0.1}
          onChangeX={(v: number) => preview({offsetU: v})}
          onChangeY={(v: number) => preview({offsetV: -v})}
          onCommitX={(v: number) => commit({offsetU: v})}
          onCommitY={(v: number) => commit({offsetV: -v})}
          onDragEnd={endDrag}
        />
      ) : (
        <SliderRow
          label={t('device.customDecal.alongEdge')}
          value={alongValue}
          min={-alongLim}
          max={alongLim}
          step={0.1}
          unit={unit}
          onChange={(v: number) => preview({[alongKey]: v})}
          onChangeCommitted={(v: number) => {
            commit({[alongKey]: v})
            endDrag()
          }}
        />
      )}

      <FormControlLabel
        control={<Checkbox checked={decal.unlit} onChange={handleToggleUnlit} size="small" sx={sx.checkbox} />}
        label={<Typography sx={sx.label}>{t('sprite3d.unlit')}</Typography>}
        sx={sx.checkboxLabel}
      />

      {!decal.unlit && (
        <>
          <Typography sx={sx.sectionLabel}>{t('sprite3d.material')}</Typography>
          <SliderRow
            label={t('device.materials.metalness')}
            value={decal.material.metalness}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={(v: number) => handleMaterialChange({metalness: v})}
            onChangeCommitted={handleSealUndo}
          />
          <SliderRow
            label={t('device.materials.roughness')}
            value={decal.material.roughness}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={(v: number) => handleMaterialChange({roughness: v})}
            onChangeCommitted={handleSealUndo}
          />
          <SliderRow
            label={t('device.materials.clearcoat')}
            value={decal.material.clearcoat}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={(v: number) => handleMaterialChange({clearcoat: v})}
            onChangeCommitted={handleSealUndo}
          />
          <SliderRow
            label={t('device.materials.clearcoatRoughness')}
            value={decal.material.clearcoatRoughness}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={(v: number) => handleMaterialChange({clearcoatRoughness: v})}
            onChangeCommitted={handleSealUndo}
          />
          <SliderRow
            label={t('device.materials.reflectivity')}
            value={decal.material.reflectivity}
            min={0}
            max={1}
            step={0.01}
            unit=""
            onChange={(v: number) => handleMaterialChange({reflectivity: v})}
            onChangeCommitted={handleSealUndo}
          />
        </>
      )}
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  label: {
    fontSize: 11,
    color: tokens.text.secondary,
    whiteSpace: 'nowrap' as const,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.muted,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    marginTop: '8px',
    marginBottom: '4px',
  },
  checkboxLabel: {
    marginTop: '4px',
    marginBottom: '0px',
    marginLeft: '-4px',
  },
  checkbox: {
    color: tokens.text.muted,
    '&.Mui-checked': {
      color: tokens.text.primary,
    },
    padding: '4px',
  },
})

export default CustomDecalItem
